import { useState, useImperativeHandle, forwardRef, useCallback } from 'react'
import { router } from '@inertiajs/react'
import CreateFolderModal from '@features/file-operations/components/CreateFolderModal'
import DocumentUploadModal from '@features/file-operations/components/DocumentUploadModal'
import ConfirmDeleteModal from '@components/ConfirmDeleteModal'
import RenameModal from '@components/RenameModal'
import PermissionsModal from '@features/permissions/components/PermissionsModal'
import { FileExplorerItem } from '@features/file-explorer/types'
import { useSelectionStore } from '@stores/useSelectionStore'

interface FileExplorerModalsProps {
  currentFolderId: string | null
}

export interface FileExplorerModalsHandle {
  openFolderModal: () => void
  openUploadModal: () => void
  openDeleteModal: (item: FileExplorerItem) => void
  openBulkDeleteModal: (items: FileExplorerItem[]) => void
  openRenameModal: (item: FileExplorerItem) => void
  openPermissionsModal: (item: FileExplorerItem) => void
}

const FileExplorerModals = forwardRef<FileExplorerModalsHandle, FileExplorerModalsProps>(
  ({ currentFolderId }, ref) => {
    // Folder creation modal state
    const [isFolderModalOpen, setIsFolderModalOpen] = useState(false)

    // Document upload modal state
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

    // Delete confirmation modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<FileExplorerItem | null>(null)
    const [itemsToDelete, setItemsToDelete] = useState<FileExplorerItem[]>([])
    const [isDeleting, setIsDeleting] = useState(false)

    // Rename modal state
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
    const [itemToRename, setItemToRename] = useState<FileExplorerItem | null>(null)

    // Permissions modal state
    const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false)
    const [itemToManagePermissions, setItemToManagePermissions] = useState<FileExplorerItem | null>(null)

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      openFolderModal: () => setIsFolderModalOpen(true),
      openUploadModal: () => setIsUploadModalOpen(true),
      openDeleteModal: (item: FileExplorerItem) => {
        setItemToDelete(item)
        setItemsToDelete([])
        setIsDeleteModalOpen(true)
      },
      openBulkDeleteModal: (items: FileExplorerItem[]) => {
        setItemToDelete(null)
        setItemsToDelete(items)
        setIsDeleteModalOpen(true)
      },
      openRenameModal: (item: FileExplorerItem) => {
        setItemToRename(item)
        setIsRenameModalOpen(true)
      },
      openPermissionsModal: (item: FileExplorerItem) => {
        setItemToManagePermissions(item)
        setIsPermissionsModalOpen(true)
      }
    }))

    // Delete handlers
    const handleConfirmDelete = useCallback(() => {
      if (!itemToDelete) return

      setIsDeleting(true)
      const endpoint = itemToDelete.is_folder
        ? `/folders/${itemToDelete.id}`
        : `/documents/${itemToDelete.id}`

      // Get the item key for selection removal
      const itemKey = itemToDelete.is_folder
        ? `folder-${itemToDelete.id}`
        : `document-${itemToDelete.id}`

      router.delete(endpoint, {
        only: itemToDelete.is_folder
          ? ['items', 'pagination', 'folder_tree', 'storage_usage', 'flash']
          : ['items', 'pagination', 'storage_usage', 'flash'],
        preserveScroll: true,
        onSuccess: () => {
          // Remove deleted item from selection
          const { selectedItems } = useSelectionStore.getState()
          if (selectedItems.has(itemKey)) {
            const newSelected = new Set(selectedItems)
            newSelected.delete(itemKey)
            useSelectionStore.setState({ selectedItems: newSelected })
          }

          setIsDeleteModalOpen(false)
          setItemToDelete(null)
        },
        onError: (errors) => {
          console.error('Delete error:', errors)
        },
        onFinish: () => {
          setIsDeleting(false)
        }
      })
    }, [itemToDelete])

    const handleConfirmBulkDelete = useCallback(() => {
      if (itemsToDelete.length === 0) return

      setIsDeleting(true)

      // Prepare items array for backend
      const itemsPayload = itemsToDelete.map(item => ({
        type: item.is_folder ? 'Folder' : 'Document',
        id: item.id
      }))

      // Get all item keys for selection removal
      const deletedKeys = new Set(
        itemsToDelete.map(item =>
          item.is_folder ? `folder-${item.id}` : `document-${item.id}`
        )
      )

      // Check if any folders are being deleted (need folder_tree update)
      const hasFolders = itemsToDelete.some(item => item.is_folder)

      router.post('/bulk_delete', {
        items: itemsPayload,
        current_folder_id: currentFolderId
      }, {
        // Partial reload - include folder_tree only if deleting folders
        only: hasFolders
          ? ['items', 'pagination', 'folder_tree', 'storage_usage', 'flash']
          : ['items', 'pagination', 'storage_usage', 'flash'],
        preserveScroll: true,
        onSuccess: () => {
          // Remove deleted items from selection
          const { selectedItems } = useSelectionStore.getState()
          const newSelected = new Set(
            Array.from(selectedItems).filter(key => !deletedKeys.has(key))
          )

          if (newSelected.size !== selectedItems.size) {
            useSelectionStore.setState({ selectedItems: newSelected })
          }

          setIsDeleteModalOpen(false)
          setItemsToDelete([])
        },
        onError: (errors) => {
          console.error('Bulk delete error:', errors)
        },
        onFinish: () => {
          setIsDeleting(false)
        }
      })
    }, [itemsToDelete, currentFolderId])

    const handleCloseDeleteModal = useCallback(() => {
      if (!isDeleting) {
        setIsDeleteModalOpen(false)
        setItemToDelete(null)
        setItemsToDelete([])
      }
    }, [isDeleting])

    const handleCloseRenameModal = useCallback(() => {
      setIsRenameModalOpen(false)
      setItemToRename(null)
    }, [])

    const handleClosePermissionsModal = useCallback(() => {
      setIsPermissionsModalOpen(false)
      setItemToManagePermissions(null)
    }, [])

    return (
      <>
        {/* Create Folder Modal */}
        <CreateFolderModal
          isOpen={isFolderModalOpen}
          onClose={() => setIsFolderModalOpen(false)}
          parentFolderId={currentFolderId ?? undefined}
        />

        {/* Document Upload Modal */}
        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          folderId={currentFolderId ?? undefined}
        />

        {/* Confirm Delete Modal */}
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          item={itemToDelete}
          items={itemsToDelete}
          isDeleting={isDeleting}
          onClose={handleCloseDeleteModal}
          onConfirm={itemsToDelete.length > 0 ? handleConfirmBulkDelete : handleConfirmDelete}
        />

        {/* Rename Modal */}
        <RenameModal
          isOpen={isRenameModalOpen}
          item={itemToRename}
          onClose={handleCloseRenameModal}
        />

        {/* Permissions Modal */}
        <PermissionsModal
          isOpen={isPermissionsModalOpen}
          item={itemToManagePermissions}
          onClose={handleClosePermissionsModal}
        />
      </>
    )
  }
)

FileExplorerModals.displayName = 'FileExplorerModals'

export default FileExplorerModals
